import './index.scss';

import classNames from 'classnames';
import { type FC, memo, type ReactNode, useCallback, useEffect, useId, useRef } from 'react';

import { useRightPanelContext } from '../../../context/right-section';
import type { IUsePanelHistory } from '../../../hooks/panel-history';
import { ActionComponent } from '../action-component';
import { TypeButton } from '../button';
import { RefreshComponent } from '../Refresh';
import { NavArrowLeftSVG, NavArrowRightSVG, PulseSVG } from '../svgs';
import TextPrompt from '../text-prompt';

export interface IRSection {
	children?: any;
	panelHistoryProps: IUsePanelHistory;
}

// Get all focusable elements within a container
const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
	const focusableSelectors = [
		'button:not([disabled])',
		'[href]',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join(', ');

	return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
};

const RightPanel: FC<IRSection> = ({ children, panelHistoryProps }) => {
	const rightPanel = useRightPanelContext();
	const panelRef = useRef<HTMLDivElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);
	const titleId = useId();

	const isTitleString = typeof rightPanel?.title === 'string';
	const isOpen = rightPanel?.openSection;

	// A panel may veto chrome-driven dismissal (Escape / backdrop / X / history arrows) — e.g.
	// a half-written justification on the register panel. Direct closeSection() calls do not
	// come through here, so a panel closing itself is never vetoed.
	const runGuarded = useCallback(
		(finish: () => void) => {
			const guard = rightPanel?.beforeCloseRef?.current;
			if (!guard) {
				finish();
				return;
			}
			void Promise.resolve(guard()).then((proceed) => {
				if (proceed) finish();
			});
		},
		[rightPanel]
	);

	const closeRightSection = useCallback(() => {
		runGuarded(() => {
			panelHistoryProps?.reset();
			rightPanel?.closeSection?.();
		});
	}, [panelHistoryProps, rightPanel, runGuarded]);

	// Back / Forward leave the current panel too, so a draft there is just as lost.
	const goBack = () => runGuarded(panelHistoryProps.goBack);
	const goForward = () => runGuarded(panelHistoryProps.goForward);

	// Improved ctaLoad logic
	const ctaLoad = rightPanel?.cta.reduce(
		(acc, { load, loadText, responseText, responseStatus }) => ({
			status: acc.status || !!load,
			text: loadText || acc.text,
			response: responseText || acc.response,
			responseStatus: responseStatus ?? acc.responseStatus,
		}),
		{ status: false, text: '', response: '', responseStatus: false }
	);

	// Handle keyboard navigation
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!isOpen) return;

			// Close on Escape key
			if (event.key === 'Escape') {
				event.preventDefault();
				closeRightSection();
				return;
			}

			// Trap focus within panel
			if (event.key === 'Tab' && panelRef.current) {
				const focusableElements = getFocusableElements(panelRef.current);
				if (focusableElements.length === 0) return;

				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				if (event.shiftKey) {
					if (document.activeElement === firstElement) {
						event.preventDefault();
						lastElement.focus();
					}
				} else {
					if (document.activeElement === lastElement) {
						event.preventDefault();
						firstElement.focus();
					}
				}
			}
		},
		[isOpen, closeRightSection]
	);

	/**
	 * Always-current handler behind a stable identity.
	 *
	 * `useRightPanelContext()` returns a fresh object every render, so `closeRightSection`
	 * and therefore `handleKeyDown` are new on every render too. Depending on the handler
	 * below made the focus effect re-run constantly — see the note on that effect.
	 */
	const handleKeyDownRef = useRef(handleKeyDown);
	handleKeyDownRef.current = handleKeyDown;

	// F4.05 / F4.06 — capture the trigger element on open, restore on close,
	// and prefer auto-focusing the panel's primary input over the close button.
	// Priority on open:
	//   1. an element marked `data-autofocus` (an opinionated escape hatch),
	//   2. the first text-like form input in the body (input/textarea/select),
	//   3. the close button (legacy fallback so the panel never opens
	//      focus-less).
	// Restoration on close uses `previousFocusRef` so keyboard users return to
	// the trigger row in the underlying table or list.
	//
	// Keyed on `isOpen` ALONE, which is what "on open" above has always meant. It
	// previously also depended on `handleKeyDown`, which is a new function on every render
	// (see above) — so this ran on every render, and each run armed a fresh 300 ms timer
	// that moved focus again. Any panel updating its CTA while the user typed therefore
	// had focus yanked back to its first field a third of a second later, and a panel
	// marking `data-autofocus` had it yanked there repeatedly. The listener still sees the
	// current handler through the ref, so Escape and the Tab trap are unchanged.
	useEffect(() => {
		if (isOpen) {
			previousFocusRef.current = document.activeElement as HTMLElement;
			const onKeyDown = (event: KeyboardEvent) => handleKeyDownRef.current(event);
			document.addEventListener('keydown', onKeyDown);

			const timer = setTimeout(() => {
				if (!panelRef.current) return;
				const body = panelRef.current.querySelector<HTMLElement>('.rs-body') ?? panelRef.current;
				const opinionatedTarget = panelRef.current.querySelector<HTMLElement>('[data-autofocus]');
				const firstInput = body.querySelector<HTMLElement>(
					'input:not([type="hidden"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled])'
				);
				const fallback = panelRef.current.querySelector<HTMLButtonElement>('[data-close-button]');
				const target = opinionatedTarget ?? firstInput ?? fallback;
				target?.focus();
			}, 300);

			return () => {
				document.removeEventListener('keydown', onKeyDown);
				clearTimeout(timer);
			};
		}
		if (previousFocusRef.current) {
			previousFocusRef.current.focus();
			previousFocusRef.current = null;
		}
		return undefined;
	}, [isOpen]);

	return (
		<>
			{/* Backdrop with aria-hidden */}
			<div
				className={`back-drop ${isOpen ? 'open' : 'close'} ${rightPanel?.quiet ? 'back-drop--quiet' : ''}`}
				onClick={closeRightSection}
				aria-hidden='true'
			/>

			{/* Main panel with accessibility attributes */}
			<div
				ref={panelRef}
				className={classNames('right_container', {
					menuopen: isOpen,
					menuclose: !isOpen,
					max: rightPanel?.max,
					compact: rightPanel?.compact,
					half: rightPanel?.half,
				})}
				role='dialog'
				aria-modal='true'
				aria-labelledby={titleId}
				aria-hidden={!isOpen}
				tabIndex={-1}
			>
				<div className='rs-header border-label-bottom gap-20'>
					<nav
						className='mt-auto py-3 f-row-26'
						aria-label='Panel history navigation'
					>
						<button
							type='button'
							aria-label='Go back in panel history'
							disabled={!panelHistoryProps.canGoBack}
							onClick={goBack}
							style={{
								background: 'transparent',
								border: 'none',
								padding: '0.25rem',
								cursor: panelHistoryProps.canGoBack ? 'pointer' : 'not-allowed',
							}}
						>
							<NavArrowLeftSVG
								color={panelHistoryProps.canGoBack ? '' : 'lightgrey'}
								aria-hidden='true'
							/>
						</button>

						<button
							type='button'
							aria-label='Go forward in panel history'
							disabled={!panelHistoryProps.canGoForward}
							onClick={goForward}
							style={{
								background: 'transparent',
								border: 'none',
								padding: '0.25rem',
								cursor: panelHistoryProps.canGoForward ? 'pointer' : 'not-allowed',
							}}
						>
							<NavArrowRightSVG
								color={panelHistoryProps.canGoForward ? '' : 'lightgrey'}
								aria-hidden='true'
							/>
						</button>
					</nav>

					<div className='border-label rounded p-2 w-100'>
						{rightPanel?.breadcrumb && rightPanel.breadcrumb.length > 0 ? (
							<nav
								className='panel-breadcrumb'
								aria-label='Panel context'
							>
								{rightPanel.breadcrumb.map((segment, i) => (
									<span
										key={`${segment}-${i}`}
										className='panel-breadcrumb__segment'
									>
										<span>{segment}</span>
										{i < rightPanel.breadcrumb.length - 1 ? (
											<span
												className='panel-breadcrumb__separator'
												aria-hidden='true'
											>
												›
											</span>
										) : null}
									</span>
								))}
							</nav>
						) : null}
						{isTitleString ? (
							<h2
								id={titleId}
								className='panel-title'
							>
								{rightPanel.title}
							</h2>
						) : (
							<div id={titleId}>{rightPanel?.title}</div>
						)}
					</div>

					<div className='f-row-20 align-items-center ctas ml-auto'>
						{/* F4.08 — wrap the close button in a span so the native HTML title
						    attribute provides a hover tooltip ("Close (Esc)") without
						    fighting the TypeButton's `title` prop (which is the button
						    label, not the HTML title). */}
						<span title='Close (Esc)'>
							<TypeButton
								title=''
								close
								buttonType='danger'
								buttonSize='small'
								onClick={closeRightSection}
								aria-label='Close panel (Escape)'
								data-close-button
							/>
						</span>
					</div>
				</div>

				{rightPanel?.title ? (
					<div className='rs-body position-relative'>
						<ChildComponents>{children}</ChildComponents>
						{rightPanel?.cta.length > 0 && (
							<div
								className='position-fixed bg-surface border-label-top shadow w-100 py-3 justify-content-between align-items-center px-4'
								style={{ bottom: 0, left: 0, display: 'flex', zIndex: 50 }}
							>
								<div
									className='mx-auto f-row-17'
									style={{ width: '94%' }}
								>
									{rightPanel.cta.map((i, index) => (
										<div key={index}>
											{i.actionType !== 'multiple' ? (
												<TypeButton
													title={i.title}
													buttonType={ctaLoad?.status ? 'disabled' : i.buttonType}
													buttonSize='small'
													onClick={i.action}
													aria-describedby={i.describedBy}
												/>
											) : (
												<ActionComponent
													title={i.title}
													actions={i.actionOptions?.map(({ title, action }) => ({
														label: title,
														action,
													}))}
													buttonType={i.buttonType}
													buttonSize='small'
												/>
											)}
										</div>
									))}
								</div>

								<div style={{ flexShrink: 0 }}>
									{ctaLoad?.status ? (
										<RefreshComponent
											load={ctaLoad.status}
											text={ctaLoad.text}
										/>
									) : (
										<TextPrompt
											prompt={ctaLoad?.response}
											status={ctaLoad?.responseStatus}
										/>
									)}
								</div>
							</div>
						)}
					</div>
				) : (
					<div className='pt-3'>
						{!rightPanel?.data ? (
							<TextPrompt
								prompt='Something went wrong'
								status={false}
							/>
						) : (
							<PulseSVG />
						)}
					</div>
				)}
			</div>
		</>
	);
};

export default RightPanel;

interface IChildComponent extends React.HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

export const ChildComponents: FC<IChildComponent> = memo(({ children, ...rest }) => <div {...rest}>{children}</div>);

ChildComponents.displayName = 'ChildComponents';
